/**
 * Personalizzazione dell'aspetto della pagina di login in base al client.
 *
 * `LoginDetailDto.clientStyles` è un `Map<String, Any?>`: sso-bck lo memorizza e lo
 * restituisce senza guardarci dentro (colonna `Styles NVARCHAR(MAX)`, migrazione 003).
 * Non esiste quindi nessuno schema lato backend — la convenzione è questa, e va tenuta
 * allineata a mano con la form del backoffice che la scrive.
 *
 * Il valore attraversa il confine di fiducia: chi amministra il backoffice può scrivere
 * qualunque stringa e questa finisce nel CSS della pagina che raccoglie le password. Per
 * questo qui non si applica nulla che non superi una validazione severa, e le chiavi
 * ignote vengono scartate: la whitelist sotto è l'unica strada per arrivare al CSS.
 */

/** Chiave del JSON → proprietà custom CSS che alimenta. */
const COLOR_TOKENS = {
  pageBackground: '--bg',
  cardBackground: '--surface-solid',
  textPrimary: '--text',
  textSecondary: '--text-secondary',
  accent: '--accent',
  /** Testo dentro il pulsante: va scelto a parte, non si deduce dall'accent. */
  accentText: '--accent-text',
} as const;

/** Chiave che porta un'immagine di sfondo anziché un colore. */
const IMAGE_TOKEN = { pageBackgroundImage: '--page-image' } as const;

/**
 * Classe posata su `<html>` quando almeno un valore è stato applicato.
 *
 * Serve a due cose: fa scattare le derivazioni in styles.scss (bordi, campi, testo tenue,
 * anello di focus — tutti calcolati dai colori ricevuti) e spegne le decorazioni tipiche
 * dell'SSO, l'alone e il filo di luce sulla card, che sono costruite per uno sfondo scuro e
 * su una card chiara diventerebbero sporcizia.
 */
export const THEMED_CLASS = 'client-themed';

/**
 * Colori accettati: esadecimale, `rgb()`/`rgba()`, `hsl()`/`hsla()`.
 *
 * Niente parole chiave CSS, niente `var()`, niente `color-mix()`: sono superflue per una
 * palette configurata da un pannello e ognuna sarebbe un modo in più di far entrare
 * qualcosa che non è un colore. Nessun carattere che chiuda una dichiarazione — `;` `}`
 * `:` — può passare da qui.
 */
const COLOR_PATTERN =
  /^(#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|(?:rgb|hsl)a?\([0-9.,%\s/deg]+\))$/i;

/**
 * URL dell'immagine di sfondo: solo http(s) e nessun carattere che permetta di uscire
 * dalla `url("…")` in cui verrà racchiuso (apici, parentesi, spazi, backslash).
 */
const IMAGE_URL_PATTERN = /^https?:\/\/[^\s'"()\\]+$/i;

/**
 * Applica gli stili del client alla radice del documento.
 *
 * Sulla radice e non sull'host del componente perché il colore di sfondo lo dipinge
 * `body`, che sta fuori dal componente: applicandoli più in basso la pagina resterebbe
 * scura attorno a una card chiara.
 *
 * @returns `true` se almeno un valore è stato applicato.
 */
export function applyClientStyles(
  styles: Record<string, unknown> | null | undefined,
  root: HTMLElement = document.documentElement,
): boolean {
  if (styles === null || styles === undefined) {
    return false;
  }

  let applied = false;

  for (const [key, cssVariable] of Object.entries(COLOR_TOKENS)) {
    const value = readString(styles[key]);
    if (value !== null && COLOR_PATTERN.test(value)) {
      root.style.setProperty(cssVariable, value);
      applied = true;
    }
  }

  for (const [key, cssVariable] of Object.entries(IMAGE_TOKEN)) {
    const value = readString(styles[key]);
    if (value !== null && IMAGE_URL_PATTERN.test(value)) {
      // Le doppie virgolette sono sicure: il pattern sopra le ha già escluse dal valore.
      root.style.setProperty(cssVariable, `url("${value}")`);
      applied = true;
    }
  }

  if (applied) {
    root.classList.add(THEMED_CLASS);
  }

  return applied;
}

/**
 * I valori arrivano da un `Map<String, Any?>`: possono essere numeri, oggetti annidati o
 * null. Qui passano solo le stringhe non vuote, tutto il resto vale come "non impostato".
 */
function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed !== '' ? trimmed : null;
}
